import { supabase } from '@/integrations/supabase/client';
import type { ShiftConfig } from '@/types/injection';

export async function fetchShiftConfigs(orgId: string | null): Promise<ShiftConfig[]> {
  let q = supabase
    .from('shift_configs')
    .select('*')
    .order('start_hour', { ascending: true })
    .order('start_minute', { ascending: true });

  if (orgId) q = q.eq('organization_id', orgId);

  const { data, error } = await q;
  if (error) throw new Error(error.message);
  return (data ?? []) as ShiftConfig[];
}

export interface ShiftConfigInput {
  shift_label: string;
  start_hour: number;
  start_minute: number;
  end_hour: number;
  end_minute: number;
  active?: boolean;
}

export async function createShiftConfig(
  orgId: string,
  input: ShiftConfigInput,
): Promise<ShiftConfig> {
  const { data, error } = await supabase
    .from('shift_configs')
    .insert({ organization_id: orgId, ...input })
    .select()
    .single();

  if (error) throw new Error(error.message);
  return data as ShiftConfig;
}

export async function updateShiftConfig(
  id: string,
  input: Partial<ShiftConfigInput>,
): Promise<ShiftConfig> {
  const { data, error } = await supabase
    .from('shift_configs')
    .update(input)
    .eq('id', id)
    .select()
    .single();

  if (error) throw new Error(error.message);
  return data as ShiftConfig;
}

export async function deleteShiftConfig(id: string): Promise<void> {
  const { error } = await supabase.from('shift_configs').delete().eq('id', id);
  if (error) throw new Error(error.message);
}

/**
 * Returns the shift label matching the current local time, or null if none match.
 */
export function detectCurrentShift(configs: ShiftConfig[]): string | null {
  return detectCurrentShiftConfig(configs)?.shift_label ?? null;
}

/**
 * Returns the full ShiftConfig for the current local time, or null.
 */
export function detectCurrentShiftConfig(configs: ShiftConfig[]): ShiftConfig | null {
  const now = new Date();
  const totalMin = now.getHours() * 60 + now.getMinutes();

  for (const c of configs) {
    if (!c.active) continue;
    const start = c.start_hour * 60 + c.start_minute;
    const end   = c.end_hour   * 60 + c.end_minute;

    if (start <= end) {
      if (totalMin >= start && totalMin < end) return c;
    } else {
      // Overnight shift (e.g. 22:00 → 06:00)
      if (totalMin >= start || totalMin < end) return c;
    }
  }
  return null;
}

/**
 * Returns the Date representing the start of the given shift on the appropriate calendar day.
 * Handles overnight shifts correctly.
 */
export function getShiftWindowStart(config: ShiftConfig): Date {
  const now   = new Date();
  const start = new Date(now);
  start.setHours(config.start_hour, config.start_minute, 0, 0);

  const startMin = config.start_hour * 60 + config.start_minute;
  const endMin   = config.end_hour   * 60 + config.end_minute;
  const nowMin   = now.getHours() * 60 + now.getMinutes();

  // For an overnight shift, if we're in the post-midnight portion the shift started yesterday
  if (startMin > endMin && nowMin < endMin) {
    start.setDate(start.getDate() - 1);
  }
  return start;
}
